import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import 'react-datepicker/dist/react-datepicker.css';
import { saveEvent, updateEvent } from '../../services/supabase';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import EventAssignUserForm from './EventAssignUserForm';
import EventAssignEquipment from './EventAssignEquipment';
import { supabase,  addEventContacts, getEquipment, assignUsersToEvent, addEquipmentToEvent, fetchDayEvents, deleteEvent, makeGoogleApiRequest } from '../../services/supabase';
import { getTimezoneForLocation } from '../../utils/utils';
import { MapPin } from 'lucide-react';
import EventStatusBadge from './EventStatusBadge';
import { PrimaryButton, DefaultButton } from '../Common/BaseButton';
import { DateTime } from 'luxon';
import Select from 'react-select';
import EventContacts from './EventContactForm';
// import { sendPreEventEmail as sendPreEventEmails } from '../../services/emailService';

const FormContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    border-color: var(--primary-green);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    border-color: var(--primary-green);
    outline: none;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: var(--primary-green);
  margin-top: 0.5rem;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const NativeDateInput = styled.input`
  border: 1px solid #d1e3dd;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  background: #fff;
  color: #222;
  transition: border 0.2s;
  outline: none;
  width: 100% !important;

  &:focus {
    border-color: var(--primary-green, #2D882D);
    box-shadow: 0 0 0 2px #e6f4ea;
  }

  @media (max-width: 600px) {
    font-size: 0.95rem;
    padding: 0.4rem 0.7rem;
    min-width: 300px;
    text-align: start;
  }
`;

const NativeTimeInput = styled(NativeDateInput).attrs({ type: 'time' })``;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const EventList = styled.div`
  margin: 1rem 0;
  max-height: 300px;
  overflow-y: auto;
`;

const EventItem = styled.div`
  padding: 0.75rem;
  border: 1px solid #d1e3dd;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;
`;

const CustomSelect = styled.div`
  position: relative;
  width: 100%;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 0.3rem .6rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1rem;
  color: #333;

  &:hover {
    border-color: #bbb;
  }

  &:focus {
    outline: none;
    border-color: var(--primary-green);
    box-shadow: 0 0 0 2px rgba(45, 136, 45, 0.1);
  }
`;

const SelectDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-top: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
`;

const SelectOption = styled.div`
  padding: 0.3rem .6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
  will-change: transform, background-color;
  z-index: 1000;
  pointer-events: auto;
  position: relative;

  &:hover {
    background-color: #f9fafb;
  }

  ${props => props.$selected && `
    background-color: #f0fdf4;
    border-left: 3px solid var(--primary-green);
  `}
`;

const ChevronDown = styled.span`
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #666;
  margin-left: 0.5rem;
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputGroupAddon = styled.div`
  position: absolute;
  left: 12px;
  color: #666;
  font-weight: 500;
  z-index: 1;
  pointer-events: none;
`;

const InputWithAddon = styled(Input)`
  padding-left: 30px;
`;

const formatCurrency = (value) => {
  if (!value) return '';
  // Remove all non-numeric characters except decimal point
  const numericValue = value.replace(/[^\d.]/g, '');
  
  // Split by decimal point
  const parts = numericValue.split('.');
  
  // Add commas to the integer part
  if (parts[0]) {
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  
  // Rejoin with decimal point
  return parts.join('.');
};

const parseCurrency = (value) => {
  // Remove commas and return clean number
  return value.replace(/,/g, '');
};

const EventForm = ({ onSuccess }) => {
  const [title, setTitle] = useState('');
  const [eventDate, setEventDate] = useState(new Date()); // This will be the start date
  const [endDate, setEndDate] = useState(new Date()); // New state for end date
  const [isMultiDay, setIsMultiDay] = useState(false); // New state to toggle multi-day mode
  const defaultStart = new Date(eventDate);
  defaultStart.setHours(12, 0, 0, 0);
  const defaultEnd = new Date(eventDate);
  defaultEnd.setHours(14, 0, 0, 0);
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);
  const [location, setLocation] = useState('');
  const [locationValue, setLocationValue] = useState(null);
  const [locationCoords, setLocationCoords] = useState({
    lat: null,
    lng: null,
    placeId: null
  });
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [assignedTeam, setAssignedTeam] = useState([]);
  const [company, setCompany] = useState('');
  const [expectedRevenue, setExpectedRevenue] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [dayEvents, setDayEvents] = useState([]);
  const [eventStatus, setEventStatus] = useState('In_Progress');
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [eventTimezone, setEventTimezone] = useState('UTC');
  const [accountRep, setAccountRep] = useState(null);
  const [isVenueTypeDropdownOpen, setIsVenueTypeDropdownOpen] = useState(false);
  const [venueType, setVenueType] = useState('');
  const [isEventTypeDropdownOpen, setIsEventTypeDropdownOpen] = useState(false);
  const [eventType, setEventType] = useState('');
  // const [sendPreEventEmail, setSendPreEventEmail] = useState(false);
  const handleLocationSelect = async (place) => {
    setLocationValue(place);

    if (place && place.value) {
      setLocation(place.label);
      
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails(
        {
          placeId: place.value.place_id,
          fields: ['geometry', 'formatted_address']
        },
        async (placeResult, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const lat = placeResult.geometry.location.lat();
            const lng = placeResult.geometry.location.lng();
            
            setLocationCoords({
              lat,
              lng,
              placeId: place.value.place_id
            });
            
            // Get timezone for the location
            const timezone = await getTimezoneForLocation(lat, lng);
            setEventTimezone(timezone);
            
            // Reset times to default in the new timezone
            const dateStr = eventDate.toISOString().split('T')[0];
            const defaultStart = DateTime.fromISO(`${dateStr}T12:00:00`, { zone: timezone });
            const defaultEnd = DateTime.fromISO(`${dateStr}T14:00:00`, { zone: timezone });
            
            setStartTime(defaultStart.toJSDate());
            setEndTime(defaultEnd.toJSDate());
            
            setLocation(placeResult.formatted_address);
          }
        }
      );
    } else {
      // If location is cleared, reset everything
      setLocationCoords({ lat: null, lng: null, placeId: null });
      setEventTimezone('UTC');
      setStartTime(null);
      setEndTime(null);
    }
  };
  
  const employeeOptions = useMemo(
    () =>
      (allEmployees || []).map(emp => {
        const label = `${emp.name} (${emp.email})`;
        return { value: emp.id, label, _l: label.toLowerCase() };
      }),
    [allEmployees]
  );

  // Updated handleTimeInput function to work with different dates
  const handleTimeInput = (timeValue, isStartTime, dateToUse = null) => {
    if (!eventTimezone) return;
    
    // Use the appropriate date - eventDate for start time, endDate for end time (if multi-day)
    const targetDate = dateToUse || (isStartTime ? eventDate : (isMultiDay ? endDate : eventDate));
    if (!targetDate) return;
    
    const dateStr = targetDate.toISOString().split('T')[0];
    const [hours, minutes] = timeValue.split(':').map(Number);
    const timeInEventTZ = DateTime.fromObject({
      year: parseInt(dateStr.split('-')[0]),
      month: parseInt(dateStr.split('-')[1]),
      day: parseInt(dateStr.split('-')[2]),
      hour: hours,
      minute: minutes
    }, { zone: eventTimezone });
    
    if (isStartTime) {
      setStartTime(timeInEventTZ.toJSDate());
    } else {
      setEndTime(timeInEventTZ.toJSDate());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !eventDate || !startTime || !endTime || !location || !venueType || !eventType) {
      setError('Please fill out all required fields.');
      return;
    }
    
    // For multi-day events, also check end date
    if (isMultiDay && !endDate) {
      setError('Please select an end date for multi-day events.');
      return;
    }
    
    const startDateTime = DateTime.fromJSDate(startTime).setZone(eventTimezone);
    const endDateTime = DateTime.fromJSDate(endTime).setZone(eventTimezone);
    
    if (startDateTime >= endDateTime) {
      setError('Start time must be before end time.');
      return;
    }

    // Remove the same-day validation for multi-day events
    if (!isMultiDay && startDateTime.startOf('day').toISO() !== endDateTime.startOf('day').toISO()) {
      setError('Start and end times must be on the same day for single-day events.');
      return;
    }
    
    // For multi-day events, validate that end date is not before start date
    if (isMultiDay) {
      const startDate = DateTime.fromJSDate(eventDate);
      const endDateDt = DateTime.fromJSDate(endDate);
      
      if (endDateDt < startDate) {
        setError('End date cannot be before start date.');
        return;
      }
    }
    
    try {
      setLoading(true);
      setError(null);

      const eventData = {
        title,
        event_date: DateTime.fromJSDate(eventDate).toFormat('yyyy-MM-dd'), // Always use start date
        start_time: startDateTime.toUTC().toISO(),
        end_time: endDateTime.toUTC().toISO(),
        location,
        location_lat: locationCoords.lat,
        location_lng: locationCoords.lng,
        location_place_id: locationCoords.placeId,
        timezone: eventTimezone,
        description,
        company_name: company,
        expected_revenue: expectedRevenue ? parseFloat(expectedRevenue) : null,
        account_rep_id: accountRep ? accountRep.value : null,
        venue_type: venueType,
        event_type: eventType,
        event_status: eventStatus,
        created_at: DateTime.now().toUTC().toISO(),
      };

      const { data, error } = await saveEvent(eventData);
      
      if (error) throw error;
      else{
        const eventId = data.id;
        //Add team members to the event
        const teamMembers = assignedTeam.map(member => ({
          ...member,
          event_id: eventId
        }));
        const { data: teamMembersData, error: teamMembersError } = await assignUsersToEvent(eventId, teamMembers);
        if (teamMembersError) throw teamMembersError;

        //Add event contacts to the event
        const eventContacts = contacts.map(contact => ({
          ...contact,
          event_id: eventId
        }));
        const { data: eventContactsData, error: eventContactsError } = await addEventContacts(eventContacts);
        if (eventContactsError) throw eventContactsError;
        
        //Add equipment to the event
        const eventEquipment = selectedEquipment.map(equipment => ({
          ...equipment,
          event_id: eventId
        }));
        const { data: eventEquipmentData, error: eventEquipmentError } = await addEquipmentToEvent(eventId, eventEquipment);
        if (eventEquipmentError) throw eventEquipmentError;

        await createGoogleCalendarEvent(eventData, teamMembers, eventContacts, eventEquipment, eventId, eventTimezone);

        // if (sendPreEventEmail && contacts && contacts.length > 0) {
        //   try {
        //     await sendPreEventEmails(eventData, contacts);
        //     console.log('Pre-event emails sent successfully');
        //   } catch (emailError) {
        //     console.error('Failed to send pre-event emails:', emailError);
        //     // Don't fail the entire event creation if email fails
        //   }
        // }
      }
      
      setSuccess(true);
      resetForm();
      
      if (onSuccess && data) {
        onSuccess(data);
      }
      
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to save event.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createGoogleCalendarEvent = async (eventDetails, teamMembers, eventContacts, eventEquipment, eventId, timezone) => {
    try {
      // Build a detailed description
      const equipmentList = eventEquipment.length > 0 
        ? eventEquipment.map(eq => `  • ${eq.label} (${eq.quantity ? eq.quantity : '1'})`).join('\n')
        : '';
        
      const contactsList = eventContacts.length > 0
        ? eventContacts.map(contact => 
            `  • ${contact.name}${contact.role ? ` (${contact.role})` : ''}` +
            `${contact.email ? `\n    Email: ${contact.email}` : ''}` +
            `${contact.phone ? `\n    Phone: ${contact.phone}` : ''}`
          ).join('\n')
        : '';

      // Build sections only if they have content
      const sections = [];
      
      if (equipmentList) {
        sections.push(`<b>Equipment:</b>\n${equipmentList}`);
      }
      
      if (contactsList) {
        sections.push(`<b>Contacts:</b>\n${contactsList}`);
      }
      
      if (eventDetails.description && eventDetails.description.trim()) {
        sections.push(`<b>Event Notes:</b>\n${eventDetails.description}`);
      }

      const detailedDescription = sections.join('\n\n');

      const startDateTime = DateTime.fromISO(eventDetails.start_time).setZone(timezone);
      const endDateTime = DateTime.fromISO(eventDetails.end_time).setZone(timezone);

      const googleEvent = {
        summary: eventDetails.title,
        location: eventDetails.location,
        description: detailedDescription,
        start: {
          dateTime: startDateTime.toISO(),
          timeZone: timezone,
        },
        end: {
          dateTime: endDateTime.toISO(),
          timeZone: timezone,
        },
        attendees: teamMembers.map(member => {
          const emailRegex = /\(([^)]+)\)/;
          const match = member.label.match(emailRegex);
          const email = match && match[1] ? match[1] : null;
          return { email: email };
        }),
      };
      // Add WeGo Support to each google event and email list
      googleEvent.attendees.push({
        email: "support@wego.golf"
      });
      teamMembers.push({
        label: "WeGoGolf Support (support@wego.golf)",
        event_id: eventId
      });
      const response = await makeGoogleApiRequest('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
        method: 'POST',
        body: JSON.stringify(googleEvent),
      });

      if (!response.ok) {
        throw new Error('Failed to create Google Calendar event');
      }

      const result = await response.json();

      // Update the event with Google Calendar information
      const { data: updatedEvent, error: eventError } = await updateEvent(eventId, { 
        google_event_link: result.htmlLink, 
        google_event_id: result.id 
      });
      
      if (eventError) throw eventError;

      // Send email notification
      await sendEmailToAttendees(teamMembers, result.htmlLink, eventDetails.title, eventId);

    } catch (error) {
      console.error('Error creating Google Calendar event:', error);
      if (error.message.includes('No valid Google access token available')) {
        alert('Your Google session has expired. Please log out and log back in to continue.');
        const { error: deleteError } = await deleteEvent(eventId);
        if (deleteError) throw deleteError;
        window.location.href = '/login';
      }
      throw error;
    }
  };
  
  const resetForm = () => {
    setTitle('');
    setEventDate(new Date());
    setStartTime(defaultStart);
    setEndTime(defaultEnd);
    setLocation('');
    setLocationValue(null);
    setLocationCoords({
      lat: null,
      lng: null,
      placeId: null
    });
    setDescription('');
    setCompany('');
    setExpectedRevenue('');
    setEventStatus('In_Progress');
    setAssignedTeam([]);
    setSelectedEquipment([]);
    setEquipment([]);
    setContacts([]);
    setEventType('');
    // setSendPreEventEmail(false);
  };

  const handleQuantityChange = (id, quantity) => {
    setSelectedEquipment(equipment =>
      equipment.map(eq =>
        eq.value === id ? { ...eq, quantity } : eq
      )
    );
  };

  useEffect(() => {
    async function fetchEmployees() {
      const { data: allUsers, error: usersError } = await supabase.rpc('get_users_with_name');
      if (!usersError) setAllEmployees(allUsers || []);
    }
    async function fetchEquipment() {
      const { data, error } = await getEquipment();
      if (error) throw error;
      setEquipment(data);
    }
    fetchEmployees();
    fetchEquipment();
  }, []);

  const sendEmailToAttendees = async (attendees, calendarLink, eventTitle, eventId) => {
    const emailContentTemplate = (recipientName) => `
<html>
<body>
<p>Hi ${recipientName},</p>

<p>You have been added to the following event: <strong>${eventTitle}</strong>.</p>

<p>You can view it on your <a href="${calendarLink}">Google Calendar</a> or view it on the <a href="https://wegogolf.pages.dev/events/${eventId}">WeGo App</a>.</p>

<p>Best regards,<br>
WeGo Golf</p>
</body>
</html>
`;

    for (const attendee of attendees) {
      const emailRegex = /\(([^)]+)\)/;
      const match = attendee.label.match(emailRegex);
      const recipientEmail = match && match[1] ? match[1] : null;
      const recipientName = attendee.label.replace(emailRegex, '').trim();
      const emailContent = emailContentTemplate(recipientName || 'There');
      const email = [
        `To: ${recipientEmail}`,
        'Subject: New Event Created',
        'Content-Type: text/html; charset="UTF-8"',
        'MIME-Version: 1.0',
        'Content-Transfer-Encoding: 7bit',
        '',
        emailContent
      ].join('\n');

      const base64EncodedEmail = btoa(email).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      try {
        const response = await makeGoogleApiRequest('https://www.googleapis.com/gmail/v1/users/me/messages/send', {
          method: 'POST',
          body: JSON.stringify({
            raw: base64EncodedEmail
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to send email to ${recipientName} (${recipientEmail})`);
        }

        const result = await response.json();
        console.log(`Email sent to ${recipientName} (${recipientEmail}):`, result);
      } catch (error) {
        console.error(`Error sending email to ${recipientName} (${recipientEmail}):`, error);
      }
    }
  };

  // Updated handleEventDateChange
  const handleEventDateChange = async (date, isEndDate = false) => {
    if (!date) {
      if (isEndDate) {
        setEndDate(null);
      } else {
        setEventDate(null);
        setStartTime(null);
        setEndTime(null);
      }
      return;
    }

    const newDate = DateTime.fromJSDate(date);
    
    if (isEndDate) {
      setEndDate(newDate.toJSDate());
      
      // Update end time to use the new end date
      if (endTime) {
        const endDt = DateTime.fromJSDate(endTime);
        setEndTime(newDate.set({ hour: endDt.hour, minute: endDt.minute }).toJSDate());
      } else {
        setEndTime(newDate.set({ hour: 14, minute: 0 }).toJSDate());
      }
    } else {
      if (startTime && endTime) {
        const startDt = DateTime.fromJSDate(startTime);
        const endDt = DateTime.fromJSDate(endTime);
        
        setEventDate(newDate.toJSDate());
        setStartTime(newDate.set({ hour: startDt.hour, minute: startDt.minute }).toJSDate());
        
        // For single-day events, update end time to same date
        if (!isMultiDay) {
          setEndTime(newDate.set({ hour: endDt.hour, minute: endDt.minute }).toJSDate());
          setEndDate(newDate.toJSDate());
        }
      } else {
        setEventDate(newDate.toJSDate());
        setStartTime(newDate.set({ hour: 12, minute: 0 }).toJSDate());
        if (!isMultiDay) {
          setEndTime(newDate.set({ hour: 14, minute: 0 }).toJSDate());
          setEndDate(newDate.toJSDate());
        }
      }

      const events = await fetchDayEvents(newDate.toJSDate());
      setDayEvents(events);
      
      if (events.length > 0) {
        setShowConflictModal(true);
      }
    }
  };

  const handleStatusChange = (status) => {
    setEventStatus(status);
    setIsStatusDropdownOpen(false);
  };

  const handleVenueTypeChange = (type) => {
    setVenueType(type);
    setIsVenueTypeDropdownOpen(false);
  };

  const handleEventTypeChange = (type) => {
    setEventType(type);
    setIsEventTypeDropdownOpen(false);
  };

  // Handle multi-day toggle
  const handleMultiDayToggle = (enabled) => {
    setIsMultiDay(enabled);
    
    if (!enabled) {
      // Single day mode - set end date to same as start date
      setEndDate(eventDate);
      if (startTime && eventDate) {
        const startDt = DateTime.fromJSDate(startTime);
        const endTimeOnSameDay = DateTime.fromJSDate(eventDate).set({ 
          hour: startDt.hour + 2, 
          minute: startDt.minute 
        }).toJSDate();
        setEndTime(endTimeOnSameDay);
      }
    } else {
      // Multi-day mode - set end date to next day if not already set
      if (!endDate || endDate.getTime() === eventDate.getTime()) {
        const nextDay = new Date(eventDate);
        nextDay.setDate(nextDay.getDate() + 1);
        setEndDate(nextDay);
        
        if (eventDate) {
          const endTimeNextDay = DateTime.fromJSDate(nextDay).set({ 
            hour: 14, 
            minute: 0 
          }).toJSDate();
          setEndTime(endTimeNextDay);
        }
      }
    }
  };

  // Scroll prevention for modal
  useEffect(() => {
    if (showConflictModal) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showConflictModal]);

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="title">Event Title *</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter event title"
            required
          />
        </FormGroup>

        {/* Multi-day toggle */}
        <FormGroup>
          <Label>
            <input
              type="checkbox"
              checked={isMultiDay}
              onChange={(e) => handleMultiDayToggle(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            Multi-day event
          </Label>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="location">Location *</Label>
          <GooglePlacesAutocomplete
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
          apiOptions={{ language: 'en', region: 'us' }}
          minLengthAutocomplete={3}
          debounce={250}
          selectProps={{
            value: locationValue,
            onChange: handleLocationSelect,
            placeholder: 'Enter event location...',
            isClearable: true,
            styles: {
              control: (provided) => ({
                ...provided,
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '0.3rem',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#2D882D'
                }
              }),
              menu: (provided) => ({
                ...provided,
                borderRadius: '4px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 9999
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected 
                  ? '#2D882D' 
                  : state.isFocused 
                    ? 'rgba(45, 136, 45, 0.1)' 
                    : 'white',
                color: state.isSelected ? 'white' : '#333',
                cursor: 'pointer'
              })
            }
          }}
        />
        </FormGroup>

        {/* Company */}
        <FormGroup>
          <Label htmlFor="company">Company</Label>
          <Input
            id="company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Enter company name"
          />
        </FormGroup>

        {/* Expected Revenue */}
        <FormGroup>
          <Label htmlFor="expectedRevenue">Expected Revenue</Label>
          <InputGroup>
            <InputGroupAddon>$</InputGroupAddon>
            <InputWithAddon
              id="expectedRevenue"
              type="text"
              inputMode="decimal"
              value={formatCurrency(expectedRevenue)}
              onChange={(e) => {
                const rawValue = parseCurrency(e.target.value);
                // Only allow numbers and one decimal point
                if (rawValue === '' || /^\d*\.?\d{0,2}$/.test(rawValue)) {
                  setExpectedRevenue(rawValue);
                }
              }}
              placeholder="0.00"
            />
          </InputGroup>
        </FormGroup>

       {/* Account Rep */}
        <FormGroup>
          <Label htmlFor="accountRep">Account Rep</Label>
          {/*
            Memoize options to avoid remapping on every keystroke elsewhere in the form.
          */}
          {(() => {
            return (
              <Select
                className="react-select"
                classNamePrefix="react-select"
                options={employeeOptions}
                value={accountRep}
                onChange={setAccountRep}
                placeholder="Select account rep..."
                isClearable
                menuPortalTarget={document.body}
                filterOption={(option, rawInput) =>
                  option.data._l.includes((rawInput || '').toLowerCase())
                }
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '0.2rem',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#2D882D'
                    }
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected 
                      ? '#2D882D' 
                      : state.isFocused 
                        ? 'rgba(45, 136, 45, 0.1)' 
                        : 'white',
                    color: state.isSelected ? 'white' : '#333',
                    cursor: 'pointer'
                  })
                }}
              />
            );
          })()}
        </FormGroup>

        <TwoColumnLayout>
          <FormGroup>
            <Label htmlFor="eventDate">{isMultiDay ? 'Start Date *' : 'Event Date *'}</Label>
            <NativeDateInput
              id="eventDate"
              type="date"
              value={eventDate ? eventDate.toLocaleDateString('en-CA') : ''}
              onChange={e => {
                if (!e.target.value) {
                  handleEventDateChange(null);
                  return;
                }
                const [year, month, day] = e.target.value.split('-').map(Number);
                const newDate = new Date(year, month - 1, day, 12, 0, 0);
                handleEventDateChange(newDate);
              }}
              required
              disabled={!locationCoords.lat}
            />
          </FormGroup>
          
          {isMultiDay && (
            <FormGroup>
              <Label htmlFor="endDate">End Date *</Label>
              <NativeDateInput
                id="endDate"
                type="date"
                value={endDate ? endDate.toLocaleDateString('en-CA') : ''}
                onChange={e => {
                  if (!e.target.value) {
                    handleEventDateChange(null, true);
                    return;
                  }
                  const [year, month, day] = e.target.value.split('-').map(Number);
                  const newDate = new Date(year, month - 1, day, 12, 0, 0);
                  handleEventDateChange(newDate, true);
                }}
                min={eventDate ? eventDate.toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA')}
                required
                disabled={!locationCoords.lat}
              />
            </FormGroup>
          )}
          
          {!isMultiDay && (
            <FormGroup>
              <Label htmlFor="eventStatus">Event Status *</Label>
              <CustomSelect>
                <SelectButton 
                  type="button"
                  onClick={() => {
                    setIsStatusDropdownOpen(!isStatusDropdownOpen);
                  }}>
                  <EventStatusBadge status={eventStatus} circleSize={6} style={{fontSize: '0.75rem'}} />
                  <ChevronDown />
                </SelectButton>
                {isStatusDropdownOpen && (
                  <SelectDropdown>
                    {['Hold', 'In_Progress', 'Complete', 'Canceled'].map((status) => (
                      <SelectOption
                        key={status}
                        $selected={eventStatus === status}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleStatusChange(status);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <EventStatusBadge status={status} circleSize={6} style={{fontSize: '0.75rem'}} />
                      </SelectOption>
                    ))}
                  </SelectDropdown>
                )}
              </CustomSelect>
            </FormGroup>
          )}
        </TwoColumnLayout>

        {isMultiDay && (
          <FormGroup>
            <Label htmlFor="eventStatus">Event Status *</Label>
            <CustomSelect>
              <SelectButton 
                type="button"
                onClick={() => {
                  setIsStatusDropdownOpen(!isStatusDropdownOpen);
                }}>
                <EventStatusBadge status={eventStatus} circleSize={6} style={{fontSize: '0.75rem'}} />
                <ChevronDown />
              </SelectButton>
              {isStatusDropdownOpen && (
                <SelectDropdown>
                  {['Hold', 'In_Progress', 'Complete', 'Canceled'].map((status) => (
                    <SelectOption
                      key={status}
                      $selected={eventStatus === status}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleStatusChange(status);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <EventStatusBadge status={status} circleSize={6} style={{fontSize: '0.75rem'}} />
                    </SelectOption>
                  ))}
                </SelectDropdown>
              )}
            </CustomSelect>
          </FormGroup>
        )}

        <TwoColumnLayout>
          <FormGroup>
            <Label htmlFor="venueType">Venue Type *</Label>
            <CustomSelect>
              <SelectButton
                required
                type="button"
                onClick={() => {
                  setIsVenueTypeDropdownOpen(!isVenueTypeDropdownOpen);
                }}>
                <span>{venueType || 'Select venue type'}</span>
                <ChevronDown />
              </SelectButton>
              {isVenueTypeDropdownOpen && (
                <SelectDropdown>
                  {['Indoor', 'Outdoor'].map((type) => (
                    <SelectOption
                      key={type}
                      $selected={venueType === type}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleVenueTypeChange(type);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <span>{type}</span>
                    </SelectOption>
                  ))}
                </SelectDropdown>
              )}
            </CustomSelect>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="eventType">Event Type *</Label>
            <CustomSelect>
              <SelectButton
                required
                type="button"
                onClick={() => {
                  setIsEventTypeDropdownOpen(!isEventTypeDropdownOpen);
                }}>
                <span>{eventType || 'Select event type'}</span>
                <ChevronDown />
              </SelectButton>
              {isEventTypeDropdownOpen && (
                <SelectDropdown>
                  {['WeGo', 'TLG'].map((type) => (
                    <SelectOption
                      key={type}
                      $selected={eventType === type}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEventTypeChange(type);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <span>{type}</span>
                    </SelectOption>
                  ))}
                </SelectDropdown>
              )}
            </CustomSelect>
          </FormGroup>
        </TwoColumnLayout>
        
        <TwoColumnLayout>
          <FormGroup>
            <Label htmlFor="startTime">Start Time *</Label>
            <NativeTimeInput
              id="startTime"
              value={startTime ? DateTime.fromJSDate(startTime)
                .setZone(eventTimezone)
                .toFormat('HH:mm') : ''}
              onChange={e => handleTimeInput(e.target.value, true)}
              required
              disabled={!locationCoords.lat}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="endTime">End Time *</Label>
            <NativeTimeInput
              id="endTime"
              value={endTime ? DateTime.fromJSDate(endTime)
                .setZone(eventTimezone)
                .toFormat('HH:mm') : ''}
              onChange={e => handleTimeInput(e.target.value, false)}
              min={!isMultiDay && startTime ? DateTime.fromJSDate(startTime)
                .setZone(eventTimezone)
                .toFormat('HH:mm') : undefined}
              required
              disabled={!locationCoords.lat}
            />
            {isMultiDay && endDate && (
              <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                End time is on {endDate.toLocaleDateString()}
              </small>
            )}
          </FormGroup>
        </TwoColumnLayout>

        <FormGroup>
          <EventAssignUserForm
            employees={allEmployees}
            assignedUsers={assignedTeam}
            onAssignChange={setAssignedTeam}
            isVisible={true}
          />
        </FormGroup>

        <FormGroup>
          <EventAssignEquipment
            equipmentList={equipment}
            selectedEquipment={selectedEquipment}
            onChange={setSelectedEquipment}
            onQuantityChange={handleQuantityChange}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="description">Event Notes</Label>
          <TextArea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter event notes and additional details"
          />
        </FormGroup>

        <EventContacts
          contacts={contacts}
          onContactsChange={setContacts}
          // sendPreEventEmail={sendPreEventEmail}
          // onSendPreEventEmailChange={setSendPreEventEmail}
        />

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>Event created successfully!</SuccessMessage>}
        
        <PrimaryButton type="submit" disabled={loading}>
          {loading ? 'Creating Event...' : 'Create Event'}
        </PrimaryButton>
      </Form>

      {showConflictModal && createPortal(
        <Modal>
          <ModalContent>
            <h3>Events Scheduled for {eventDate?.toLocaleDateString()}</h3>
            <p>The following events are already scheduled for this day:</p>
            <EventList>
              {dayEvents.map(event => (
                <EventItem key={event.id}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem',}}>
                      <strong>{event.title}</strong> <EventStatusBadge status={event.event_status} style={{fontSize: '0.6rem'}} circleSize={'6'} />
                    </div>
                    <div>
                    {new Date(event.start_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })} - 
                    {new Date(event.end_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </div>
                  {event.location && (
                    <div style={{ color: '#666', fontSize: '0.9rem' }}>
                      <MapPin size={16} /> {event.location}
                    </div>
                  )}
                </EventItem>
              ))}
            </EventList>

            <ButtonGroup>
              <DefaultButton 
                onClick={() => setShowConflictModal(false)}
              >
                Cancel
              </DefaultButton>
              <PrimaryButton 
                onClick={() => {
                  setShowConflictModal(false);
                }}
              >
                Continue Anyway
              </PrimaryButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>,
        document.body
      )}
    </FormContainer>
  );
};

export default EventForm;